/**
 * Position extraction rules for Team Shape visualization
 * Defines which points (source/destination) to use for each stat type
 */

import { VisualizationEvent } from '../components/pitch/types';

export interface PositionExtractionConfig {
  useSource?: boolean; // Optional when skip is true
  useDestination?: boolean; // Optional when skip is true
  useAllPoints?: boolean; // Use all intermediate points (for line events like dribbles)
  sourceWeight?: number; // For weighted averaging (default: 1.0)
  destinationWeight?: number; // For weighted averaging (default: 0.7)
  intermediateWeight?: number; // Weight for intermediate points (default: 0.5)
  eventWeight?: number; // Overall event importance weight (default: 1.0)
  skip?: boolean; // Skip this stat type entirely
}

export interface ExtractedPoint {
  x: number;
  y: number;
  weight: number;
}

/**
 * Comprehensive position extraction rules for all stat types
 * Based on design document: team-shape-visualization-design.md
 */
const POSITION_EXTRACTION_RULES: Record<string, PositionExtractionConfig> = {
  // Distribution - Passes (use both source and destination, higher weight for line events)
  'short_pass_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'short_pass_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'short_pass_backward_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'short_pass_backward_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'long_pass_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'long_pass_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'long_pass_backward_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'long_pass_backward_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.7, intermediateWeight: 0.5, eventWeight: 1.2 },
  'cross_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.6, intermediateWeight: 0.4, eventWeight: 1.3 },
  'cross_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.6, intermediateWeight: 0.4, eventWeight: 1.3 },
  'cross_backward_success': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.6, intermediateWeight: 0.4, eventWeight: 1.3 },
  'cross_backward_failure': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.6, intermediateWeight: 0.4, eventWeight: 1.3 },
  
  // Attack - Shots (source only, destination is goal, higher weight for important shots)
  'shot_inside_box_goal': { useSource: true, useDestination: false, eventWeight: 2.0 },
  'shot_inside_box_on_target': { useSource: true, useDestination: false, eventWeight: 1.8 },
  'shot_inside_box_off_target': { useSource: true, useDestination: false, eventWeight: 1.5 },
  'shot_inside_box_blocked': { useSource: true, useDestination: false, eventWeight: 1.3 },
  'shot_inside_box_side_post': { useSource: true, useDestination: false, eventWeight: 1.6 },
  'shot_inside_box_crossbar': { useSource: true, useDestination: false, eventWeight: 1.6 },
  'shot_outside_box_goal': { useSource: true, useDestination: false, eventWeight: 2.0 },
  'shot_outside_box_on_target': { useSource: true, useDestination: false, eventWeight: 1.6 },
  'shot_outside_box_off_target': { useSource: true, useDestination: false, eventWeight: 1.2 },
  'shot_outside_box_blocked': { useSource: true, useDestination: false, eventWeight: 1.0 },
  'shot_outside_box_side_post': { useSource: true, useDestination: false, eventWeight: 1.4 },
  'shot_outside_box_crossbar': { useSource: true, useDestination: false, eventWeight: 1.4 },
  'penalty_scored': { useSource: true, useDestination: false, eventWeight: 2.5 },
  'penalty_missed': { useSource: true, useDestination: false, eventWeight: 1.8 },
  'penalty_shootout_scored': { useSource: true, useDestination: false, eventWeight: 2.5 },
  'penalty_shootout_missed': { useSource: true, useDestination: false, eventWeight: 1.8 },
  'own_goal': { useSource: true, useDestination: false, eventWeight: 2.0 },
  
  // Defense (source only, higher weight for successful defensive actions)
  'tackle_success': { useSource: true, useDestination: false, eventWeight: 1.3 },
  'tackle_failure': { useSource: true, useDestination: false, eventWeight: 0.8 },
  'interception': { useSource: true, useDestination: false, eventWeight: 1.4 },
  'clearance': { useSource: true, useDestination: false, eventWeight: 1.1 },
  'foul_own_half': { useSource: true, useDestination: false, eventWeight: 0.9 },
  'foul_opp_half': { useSource: true, useDestination: false, eventWeight: 1.0 },
  'yellow_card': { useSource: true, useDestination: false, eventWeight: 0.7 },
  'red_card': { useSource: true, useDestination: false, eventWeight: 0.7 },
  
  // Duels - Dribbles use all points to show movement path
  'dribble_won': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.8, intermediateWeight: 0.6, eventWeight: 1.4 },
  'aerial_won': { useSource: true, useDestination: false, eventWeight: 1.1 },
  
  // Goalkeeper (source only)
  'save_open_play': { useSource: true, useDestination: false },
  'save_penalty': { useSource: true, useDestination: false },
  'open_play_claim': { useSource: true, useDestination: false },
  'open_play_punch': { useSource: true, useDestination: false },
  'penalty_claim': { useSource: true, useDestination: false },
  'penalty_punch': { useSource: true, useDestination: false },
  'gk_interception_open_play': { useSource: true, useDestination: false },
  
  // Outside - Set Pieces (higher weight for corners as they're important attacking events)
  'throw_in': { useSource: false, useDestination: true, eventWeight: 0.8 },
  'corner': { useSource: true, useDestination: true, useAllPoints: true, sourceWeight: 1.0, destinationWeight: 0.6, intermediateWeight: 0.4, eventWeight: 1.5 },
  
  // Errors
  'offside': { useSource: true, useDestination: false },
  
  // Game Flow (skip - no meaningful position)
  'substitution': { useSource: true, useDestination: false },
  'kick_off': { skip: true } as PositionExtractionConfig,
  'end_1st_half': { skip: true } as PositionExtractionConfig,
  'start_2nd_half': { skip: true } as PositionExtractionConfig,
  'start_extra_time_1st_half': { skip: true } as PositionExtractionConfig,
  'end_extra_time_1st_half': { skip: true } as PositionExtractionConfig,
  'start_extra_time_2nd_half': { skip: true } as PositionExtractionConfig,
  'penalty_shootout': { skip: true } as PositionExtractionConfig,
  'full_time': { skip: true } as PositionExtractionConfig,
};

// Fallback for unknown stat types
const DEFAULT_RULE: PositionExtractionConfig = {
  useSource: true,
  useDestination: false,
  useAllPoints: false,
  sourceWeight: 1.0,
  destinationWeight: 0.7,
  intermediateWeight: 0.5,
  eventWeight: 1.0,
  skip: false
};

/**
 * Get position extraction config for a stat type
 * Uses stat_key lookup - we'll need to fetch stat_key from stat_type_id
 */
export function getPositionExtractionConfig(statKey: string): PositionExtractionConfig {
  const config = POSITION_EXTRACTION_RULES[statKey];
  if (!config) {
    return DEFAULT_RULE;
  }
  // If skip is true, return it as-is (useSource/useDestination are optional)
  if (config.skip) {
    return config;
  }
  // Otherwise ensure all fields are set
  return {
    useSource: config.useSource ?? true,
    useDestination: config.useDestination ?? false,
    useAllPoints: config.useAllPoints ?? false,
    sourceWeight: config.sourceWeight,
    destinationWeight: config.destinationWeight,
    intermediateWeight: config.intermediateWeight,
    eventWeight: config.eventWeight ?? 1.0,
    skip: config.skip
  };
}

/**
 * Extract position points from a visualization event based on stat type rules
 * Returns array of weighted points to use for clustering
 */
export function extractTeamShapePoints(
  event: VisualizationEvent,
  statKey: string
): ExtractedPoint[] {
  const config = getPositionExtractionConfig(statKey);
  
  // Skip if configured to skip
  if (config.skip) {
    return [];
  }
  
  // No position data
  if (!event.position_data || event.position_data.length === 0) {
    return [];
  }
  
  const points: ExtractedPoint[] = [];
  const eventWeight = config.eventWeight ?? 1.0;
  const sourceWeight = (config.sourceWeight ?? 1.0) * eventWeight;
  const destinationWeight = (config.destinationWeight ?? 0.7) * eventWeight;
  const intermediateWeight = (config.intermediateWeight ?? 0.5) * eventWeight;
  
  if (event.position_data.length === 0) {
    return points;
  }
  
  // Extract source point (first point)
  if (config.useSource !== false) {
    const source = event.position_data[0];
    if (typeof source.x === 'number' && typeof source.y === 'number') {
      points.push({
        x: source.x,
        y: source.y,
        weight: sourceWeight
      });
    }
  }
  
  // Extract destination point (last point, if different from source)
  if (config.useDestination === true && event.position_data.length > 1) {
    const source = event.position_data[0];
    const destination = event.position_data[event.position_data.length - 1];
    
    // Only add if destination is different from source
    if (destination.x !== source.x || destination.y !== source.y) {
      if (typeof destination.x === 'number' && typeof destination.y === 'number') {
        points.push({
          x: destination.x,
          y: destination.y,
          weight: destinationWeight
        });
      }
    }
  }
  
  // Extract all intermediate points for line events (dribbles, passes)
  if (config.useAllPoints === true && event.position_data.length > 2) {
    // Skip first and last (already added as source/destination)
    for (let i = 1; i < event.position_data.length - 1; i++) {
      const point = event.position_data[i];
      if (typeof point.x === 'number' && typeof point.y === 'number') {
        points.push({
          x: point.x,
          y: point.y,
          weight: intermediateWeight
        });
      }
    }
  }
  
  return points;
}

/**
 * Extract all position points from multiple events
 * Useful for batch processing before clustering
 */
export function extractTeamShapePointsFromEvents(
  events: VisualizationEvent[],
  statKeyMap: Map<string, string> // Map stat_type_id -> stat_key
): ExtractedPoint[] {
  const allPoints: ExtractedPoint[] = [];
  
  for (const event of events) {
    const statKey = statKeyMap.get(event.stat_type_id);
    if (!statKey) {
      // Try to use default rule if stat key not found
      const defaultPoints = extractTeamShapePoints(event, '');
      allPoints.push(...defaultPoints);
      continue;
    }
    
    const points = extractTeamShapePoints(event, statKey);
    allPoints.push(...points);
  }
  
  return allPoints;
}

